import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Layers,
  Globe2,
  ShieldCheck,
  Headphones,
  Sliders,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { QUALITY_COMMITMENT } from "@/lib/siteData";

const differentiators = [
  {
    icon: Layers,
    title: "End-to-End Research Support",
    body: "From survey programming to final reporting — one team, one accountable process for the entire lifecycle.",
  },
  {
    icon: Globe2,
    title: "Global Reach With Local Understanding",
    body: "Cultural, linguistic, and operational nuances baked into our execution model.",
  },
  {
    icon: ShieldCheck,
    title: "Quality At Every Stage",
    body: "Respondent validation, fieldwork monitoring, and data verification embedded throughout the process.",
  },
  {
    icon: Headphones,
    title: "Dedicated Project Management",
    body: "Proactive communication, accountability, and a single point of contact across markets.",
  },
  {
    icon: Sliders,
    title: "Flexible Engagement Models",
    body: "Project-based engagements or ongoing partnership models, tailored to your operating rhythm.",
  },
];

export default function WhyUs() {
  return (
    <div data-testid="why-us-page">
      {/* HERO */}
      <section className="relative border-b border-slate-200 overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg opacity-70 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24">
          <p className="vd-eyebrow">Why VeriData Global</p>
          <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-bold text-slate-900 max-w-4xl">
            More than a vendor.{" "}
            <span className="text-teal-700">A research execution partner</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base md:text-lg text-slate-600 leading-relaxed">
            Research success depends on more than methodology. It depends on
            reliable execution, responsive support, and trusted partnerships.
            VeriData Global helps research organizations reduce operational
            complexity while improving project outcomes.
          </p>
        </div>
      </section>

      {/* DIFFERENTIATORS */}
      <section className="border-b border-slate-200">
        <div className="vd-container py-20 md:py-24">
          <div className="max-w-3xl">
            <p className="vd-eyebrow">What Makes Us Different</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Five ways we work that compound into better research outcomes.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 rounded-md overflow-hidden">
            {differentiators.map((d, i) => (
              <motion.div
                key={d.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="bg-white p-8"
              >
                <div className="flex items-center gap-3">
                  <span className="font-display text-xs text-slate-400">
                    0{i + 1}
                  </span>
                  <span className="h-px w-10 bg-slate-200" />
                </div>
                <d.icon className="h-6 w-6 text-teal-700 mt-6" />
                <h3 className="mt-4 font-display text-xl font-semibold text-slate-900">
                  {d.title}
                </h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{d.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUALITY COMMITMENTS */}
      <section className="bg-slate-50/60 border-b border-slate-200">
        <div className="vd-container py-20 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="vd-eyebrow">Our Commitment</p>
            <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-slate-900 tracking-tight leading-tight">
              Quality validation, embedded throughout the research lifecycle.
            </h2>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {QUALITY_COMMITMENT.map((q) => (
              <div
                key={q}
                className="flex items-center gap-3 border border-slate-200 rounded-md px-4 py-4 bg-white hover:border-teal-600 transition-colors"
              >
                <CheckCircle2 className="h-5 w-5 text-teal-700 flex-shrink-0" />
                <span className="text-[15px] text-slate-800">{q}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0b192c] text-white relative overflow-hidden">
        <div className="absolute inset-0 vd-grid-bg-dark opacity-50 pointer-events-none" />
        <div className="vd-container relative py-20 md:py-24 text-center">
          <p className="text-xs tracking-[0.22em] uppercase font-semibold text-teal-400">
            Let’s build better research together
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-3xl mx-auto">
            Schedule a consultation or request a proposal.
          </h2>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              data-testid="why-us-cta-consult"
              className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white font-medium px-7 py-3.5 rounded-md transition-colors shadow-sm"
            >
              Schedule a Consultation <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="why-us-cta-rfp"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/15 border border-white/20 text-white font-medium px-7 py-3.5 rounded-md transition-colors"
            >
              Request a Proposal
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
